import { useCallback, useMemo } from 'react';

import { Button, Divider, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import BigNumber from 'bignumber.js';

import { portalPoolAbi } from '@api/contracts';
import { useWriteSQDTransaction } from '@api/contracts/useWriteTransaction';
import { trpc } from '@api/trpc';
import { Card } from '@components/Card';
import { HelpTooltip } from '@components/HelpTooltip';
import { useContracts } from '@hooks/network/useContracts';
import { dollarFormatter, tokenFormatter } from '@lib/formatters/formatters';

import { ProvideButton } from '../dialogs/ProvideDialog';
import { WithdrawButton } from '../dialogs/WithdrawDialog';
import { type PoolPhase, usePoolData, usePoolUserData } from '../hooks';
import { PendingWithdrawals } from '../PendingWithdrawals';
import { invalidatePoolQueries } from '../utils/poolUtils';

interface DelegateTabProps {
  poolId: string;
}

export function DelegateTab({ poolId }: DelegateTabProps) {
  const { data: pool, isLoading: poolLoading } = usePoolData(poolId);
  const { data: userData, isLoading: userDataLoading } = usePoolUserData(poolId);
  const { SQD_TOKEN } = useContracts();
  const { data: sqdPrice } = useQuery(trpc.price.current.queryOptions());
  const queryClient = useQueryClient();
  const { writeTransactionAsync, isPending } = useWriteSQDTransaction();

  const isLoading = poolLoading || userDataLoading;

  const balance = userData?.userBalance ?? BigNumber(0);
  const balanceInUsd = sqdPrice ? balance.toNumber() * sqdPrice : undefined;

  const rewards = useMemo(
    () => (userData ? BigNumber(userData.userRewards) : BigNumber(0)),
    [userData],
  );

  const dailyRewardRate = useMemo(() => {
    if (!pool || !userData || pool.tvl.current.isZero() || pool.phase !== 'active')
      return BigNumber(0);

    // Calculate daily distribution rate: rate per second * seconds in a day / decimals
    const dailyDistribution = pool.distributionRatePerSecond.multipliedBy(86400);

    // Calculate user's share of daily rewards: daily rate * (user balance / total active stake)
    const userShare = balance.div(pool.tvl.max);

    return dailyDistribution.multipliedBy(userShare);
  }, [pool, userData, balance]);

  const hasRewards = userData?.hasRewards ?? false;

  const handleClaimRewards = useCallback(async () => {
    await writeTransactionAsync({
      address: poolId as `0x${string}`,
      abi: portalPoolAbi,
      functionName: 'claimRewards',
      args: [],
    });
    await invalidatePoolQueries(queryClient, poolId);
  }, [poolId, writeTransactionAsync, queryClient]);

  if (!pool && !isLoading) return null;

  return (
    <Stack spacing={2}>
      <Card
        title={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>Current Balance</span>
            <HelpTooltip title={`Your provided ${SQD_TOKEN} tokens in this pool.`} />
          </Stack>
        }
      >
        <Stack spacing={2} divider={<Divider />}>
          <Stack spacing={0.5}>
            <Typography variant="h5">
              {isLoading ? <Skeleton width="50%" /> : tokenFormatter(balance, SQD_TOKEN, 2)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isLoading ? <Skeleton width="50%" /> : `~${dollarFormatter(balanceInUsd || 0)}`}
            </Typography>
          </Stack>
          <Stack spacing={1}>
            <ProvideButton poolId={poolId} />
            <WithdrawButton poolId={poolId} />
          </Stack>
        </Stack>
      </Card>

      <Card
        title={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>Available Rewards</span>
            <HelpTooltip title="Accumulated rewards ready to claim." />
          </Stack>
        }
      >
        <Stack spacing={2} divider={<Divider />}>
          <Stack spacing={0.5}>
            <Typography variant="h5">
              {isLoading ? (
                <Skeleton width="50%" />
              ) : (
                tokenFormatter(rewards, pool?.rewardToken.symbol ?? '', 2)
              )}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isLoading ? (
                <Skeleton width="50%" />
              ) : (
                `${tokenFormatter(dailyRewardRate, pool?.rewardToken.symbol ?? '', 4)}/day`
              )}
            </Typography>
          </Stack>
          <Tooltip title={pool ? getClaimRewardsTooltip(pool.phase, SQD_TOKEN) : ''}>
            <span>
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={handleClaimRewards}
                disabled={!hasRewards || isPending || isLoading}
                loading={isPending}
              >
                CLAIM
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Card>
      <PendingWithdrawals poolId={poolId} />
    </Stack>
  );
}

function getClaimRewardsTooltip(phase: PoolPhase, tokenSymbol: string): string {
  switch (phase) {
    case 'collecting':
      return 'Claim your accumulated rewards. Rewards are distributed daily based on your pool share.';
    case 'idle':
      return `The reward distribution is paused because there are insufficient ${tokenSymbol} tokens provided to the pool.`;
    case 'debt':
      return 'The reward distribution is paused because the pool is out of rewards.';
    case 'failed':
    case 'closed':
      return '';
    default:
      return '';
  }
}
